import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setUser(false);
        setLoading(false);
        return;
      }
      
      const { data } = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(data);
    } catch (error) {
      localStorage.removeItem('token');
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (username, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { username, password });
    localStorage.setItem('token', data.token);
    setUser(data);
    return data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {});
    } catch (e) {
      // logout error handled silently
    }
    localStorage.removeItem('token');
    setUser(false);
  };

  const registerVendor = async (data) => {
    const response = await axios.post(`${API}/auth/register/vendor`, data);
    return response.data;
  };

  const registerSupervisor = async (data) => {
    const response = await axios.post(`${API}/auth/register/supervisor`, data);
    return response.data;
  };

  const changePassword = async (currentPassword, newPassword) => {
    const token = localStorage.getItem('token');
    const { data } = await axios.post(`${API}/auth/change-password`, {
      current_password: currentPassword,
      new_password: newPassword
    }, {
      headers: { Authorization: `Bearer ${token}` }
    });
    setUser(prev => ({ ...prev, must_change_password: false }));
    return data;
  };

  const isAuditor = user?.role === 'auditor';
  const isSupervisor = user?.role === 'supervisor';
  const isVendor = user?.role === 'vendedor';
  const canManage = isAuditor || isSupervisor;
  const mustChangePassword = user?.must_change_password === true;

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      login,
      logout,
      registerVendor,
      registerSupervisor,
      changePassword,
      checkAuth,
      isAuditor,
      isSupervisor,
      isVendor,
      canManage,
      mustChangePassword
    }}>
      {children}
    </AuthContext.Provider>
  );
};
